import { useEffect } from 'react';

import { useSystemConfig } from '@/lib/queries';

/**
 * The brand assets an admin uploaded, and getting them actually used.
 *
 * ── THE BUG THIS CLOSES ─────────────────────────────────────────────────────
 *
 * Settings → Brand has had logo, dark logo, favicon and app icon fields for
 * months. Uploading one changed nothing anybody could see: the footer was the
 * ONLY place that read `brand.logo_id`, every header drew a hardcoded SVG, and
 * index.html carried no favicon link at all.
 *
 * So the settings screen was asking people to upload files into a void. That is
 * worse than not offering the field — somebody uploads their logo, sees the
 * default, and concludes the upload failed.
 */

interface BrandAssets {
  name: string;
  tagline: string | null;
  logo: { url: string; alt: string | null } | null;
  logoDark: { url: string; alt: string | null } | null;
  favicon: string | null;
  appIcon: string | null;
}

interface ConfigBrand {
  brand?: {
    name?: string;
    tagline?: string;
    logo_id?: { url: string; alt: string | null } | null;
    logo_dark_id?: { url: string; alt: string | null } | null;
    favicon_id?: { url: string; alt: string | null } | null;
    app_icon_id?: { url: string; alt: string | null } | null;
  };
}

export function useBrand(): BrandAssets {
  const { data } = useSystemConfig();
  const brand = (data as ConfigBrand | undefined)?.brand;

  return {
    name: brand?.name ?? 'ChiCare',
    tagline: brand?.tagline ?? null,
    logo: brand?.logo_id ?? null,
    logoDark: brand?.logo_dark_id ?? null,
    favicon: brand?.favicon_id?.url ?? null,
    appIcon: brand?.app_icon_id?.url ?? null,
  };
}

/**
 * Put the uploaded favicon, app icon and name into the document.
 *
 * ── WHY THIS IS DONE AT RUNTIME ─────────────────────────────────────────────
 *
 * The favicon lives in settings, so it cannot be in index.html — that file is
 * built once and served to everybody, and changing the icon would mean a
 * deploy. The whole point of the setting is that it does not.
 *
 * The link elements are created if absent and updated if present, so this is
 * safe to call on every render of the shell and leaves nothing behind if an
 * admin clears the field.
 */
export function useBrandDocument(): void {
  const { name, favicon, appIcon } = useBrand();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    // Only the suffix is replaced. The title carries the tagline and the page
    // name, and rewriting the whole thing would throw those away.
    if (name && !document.title.includes(name)) {
      document.title = document.title.replace(/^ChiCare/, name);
    }

    setLink('icon', favicon);
    setLink('apple-touch-icon', appIcon ?? favicon);
  }, [name, favicon, appIcon]);
}

function setLink(rel: string, href: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!href) {
    // Cleared in the console: remove ours rather than leaving a stale icon
    // pointing at a deleted file, which renders as a broken image in a tab.
    existing?.remove();

    return;
  }

  const link = existing ?? document.createElement('link');
  link.rel = rel;
  link.href = href;

  if (!existing) {
    document.head.appendChild(link);
  }
}
